<script lang="ts" setup>
import { ref, watch } from 'vue'
import {
  Pin01Icon,
  Pin02Icon,
  LifeBuoy01Icon,
  Settings01Icon,
  User03Icon,
  ChevronDownIcon,
  LogOut03Icon,
  CornerDownLeftIcon,
  XIcon,
  DIcon,
  DTextField
} from 'front-library'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'

interface MenuItem {
  name: string
  icon?: string
  route?: string
  idMenu?: string
  children?: MenuItem[]
  showItems?: boolean
  openSearch?: boolean
}

interface User {
  role: string | undefined
  roleCode: string | undefined
  name: string | undefined
  photoUrl: string | undefined
  email: string | undefined
}

const props = defineProps<{
  menu?: MenuItem[]
  user?: User
}>()

const emit = defineEmits([
  'change:route',
  'change:role',
  'support',
  'config',
  'logout'
])

const { t } = useI18n()
const isPinUp = ref(false)
const subMenuItems = ref<MenuItem[]>([])
const activeRoute = ref('')
const activeMenu = ref('')
const lastActiveMenu = ref('')
const modalActive = ref(false)
const search = ref('')
const clear = ref(false)
const menuSearch = ref<any>('')
const sidebarFocused = ref(false)
const sidebar = ref<HTMLDivElement | null>(null)
const selectedSearchOptionGroup = ref('')
const selectedSearchOptionRoute = ref('')

/* Marcar sidebar como fijo o no fijo */
const pinUp = () => {
  !isPinUp.value ? (isPinUp.value = true) : (isPinUp.value = false)
}

/* Ocultar sidebar */
const onCloseSidebar = () => {
  console.log('onCloseSidebar')
  setTimeout(() => {
    if (!sidebarFocused.value) {
      console.log('El contenedor perdió el foco')
      activeMenu.value = lastActiveMenu.value
      const navSecondary = document.getElementById('nav-secondary')
      if (isPinUp.value) {
        if (navSecondary) {
          navSecondary.style.marginLeft = '0'
        }
      } else {
        if (window.innerWidth <= 639) {
          if (navSecondary) {
            navSecondary.style.marginLeft = '-224px'
          }
        } else {
          if (navSecondary) {
            navSecondary.style.marginLeft = '-288px'
          }
        }
      }
    }
  }, 200)
}

/* Mostrar sidebar */
const openSidebar = (items?: MenuItem[], menuItem?: any) => {
  activeMenu.value = menuItem.name
  if (!items) {
    if (menuItem.openSearch) {
      setTimeout(() => {
        modalActive.value = true
      }, 100)
      setTimeout(() => {
        const inputElement = document
          .getElementById('inputSearch')
          ?.querySelector('input')

        if (inputElement) {
          inputElement.focus()
        }
      }, 100)
    }
    if (menuItem.route) changeRoute(menuItem.route)
    onCloseSidebar()
    return
  }
  const childrens = document.querySelectorAll('.showItems')
  const showChildrens = document.querySelectorAll('.rotateArrow')
  for (let x = 0; x < childrens.length; x++) {
    for (let y = 0; y < showChildrens.length; y++) {
      if (
        showChildrens[y].attributes[2].value ===
        childrens[x].attributes[2].value
      ) {
        childrens[x].attributes[3].value = `height: ${
          childrens[x].children.length * 48
        }px`
        break
      } else {
        childrens[x].attributes[3].value = 'height: 0px;'
      }
    }
  }
  const navSecondary = document.getElementById('nav-secondary')
  if (navSecondary) {
    navSecondary.style.marginLeft = '0'
  }
  subMenuItems.value = items
}

/* Mostrar submenus del sidebar */
const showChildrens = (id: any, arrow: any, childrens: any) => {
  const children = document.getElementById(id)
  const arrowMenu = document.getElementById(arrow)
  if (children && arrowMenu) {
    if (children.style.height === '0px') {
      children.style.height = `${childrens * 48}px`
      arrowMenu.classList.add('rotateArrow')
    } else {
      arrowMenu.classList.remove('rotateArrow')
      children.style.height = '0px'
    }
  }
}

/* Cambiar de ruta al seleccionar una opción del sidebar */
const changeRoute = (route?: string) => {
  if (!route) return
  lastActiveMenu.value = activeMenu.value
  activeRoute.value = route
  emit('change:route', route)
}

/* Capturar cuando el menú cambia por cambio de rol para ocultar todos los hijos del menú */
watch(
  () => props.menu,
  () => {
    const hideItems = document.querySelectorAll('.showItems')
    const rotateArrows = document.querySelectorAll('.rotateArrow')
    for (let i = 0; i < hideItems.length; i++) {
      hideItems[i].attributes[3].value = 'height: 0px;'
    }
    rotateArrows.forEach(arrow => {
      arrow.classList.remove('rotateArrow')
    })
  }
)

/* Buscador del menu */
function normalize(text: string) {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}
function filterMenu(menu: any, searchText: any) {
  const normalizedSearchText = normalize(searchText.toLowerCase())
  return menu.reduce((acc: any, item: any) => {
    const newItem = { ...item }
    const normalizedItem = normalize(t(item.name).toLowerCase())
    if (item.children) {
      const filteredChildren = filterMenu(item.children, searchText)
      if (
        filteredChildren.length > 0 ||
        normalizedItem.includes(normalizedSearchText)
      ) {
        newItem.children = filteredChildren
        acc.push(newItem)
      }
    } else if (normalizedItem.includes(normalizedSearchText)) {
      acc.push(newItem)
    }
    return acc
  }, [])
}
function removeEmptyChildren(menu: any) {
  return menu.reduce((acc: any, item: any) => {
    const newItem = { ...item }
    if (item.children && item.children.length > 0) {
      newItem.children = removeEmptyChildren(item.children)
      acc.push(newItem)
    } else if (!item.children) {
      acc.push(newItem)
    }
    return acc
  }, [])
}
const searchMenu = () => {
  clear.value = true
  if (search.value === '') {
    clear.value = false
    menuSearch.value = ''
  } else {
    menuSearch.value = props.menu
    const filteredMenu = filterMenu(props.menu, search.value)
    menuSearch.value = removeEmptyChildren(filteredMenu).filter(
      (item: any) => item.children && item.children.length > 0
    )
  }

  navigateKeyUp()
}

/* Cerrar modal por teclear esc o click fuera del modal */
const closeModal = () => {
  modalActive.value = false
  menuSearch.value = ''
  clear.value = false
  search.value = ''
}
window.addEventListener(
  'keydown',
  function (event) {
    if (event.key === 'Escape' && modalActive.value) {
      closeModal()
    } else if (
      (event.ctrlKey && event.key === 'k') ||
      (event.metaKey && event.key === 'k')
    ) {
      event.preventDefault()
      modalActive.value = true
      setTimeout(() => {
        const inputElement = document
          .getElementById('inputSearch')
          ?.querySelector('input')

        if (inputElement) {
          inputElement.focus()
        }
      }, 100)
    }
  },
  false
)
document.addEventListener('click', function (event) {
  var container = document.getElementById('modalSearch')
  if (
    modalActive.value &&
    container &&
    container instanceof Node &&
    !container.contains(event.target as Node)
  ) {
    closeModal()
  }
})

const emitRole = () => {
  emit('change:role')
}
const emitSupport = () => {
  emit('support')
}
const emitConfig = () => {
  emit('config')
}
const emitLogout = () => {
  emit('logout')
}

const navigateOptions = (direction: 'up' | 'down') => {
  let options = document.querySelectorAll('.hover\\:bg-blue-600')

  if (options.length === 0) return

  let currentIndex = -1
  options.forEach((option, index) => {
    if (option.classList.contains('bg-blue-600')) {
      currentIndex = index
    }
    option.classList.remove('bg-blue-600', 'text-white')
    option.classList.add('text-gray-600')
  })

  if (direction === 'up') {
    currentIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1
  } else {
    currentIndex = currentIndex === options.length - 1 ? 0 : currentIndex + 1
  }

  options[currentIndex].classList.add('bg-blue-600', 'text-white')

  let childElement
  if (options[currentIndex].tagName === 'A') {
    childElement = options[currentIndex]
  } else {
    childElement = options[currentIndex].querySelector('a')
  }

  let indexGroup = Number(childElement?.getAttribute('group'))
  let indexTab = Number(childElement?.getAttribute('tab'))
  let indexItem = Number(childElement?.getAttribute('item'))

  let group = menuSearch.value[indexGroup]
  if (!group || !group.children) return

  let tab = group?.children[indexTab]
  if (tab.children) {
    tab = tab.children[indexItem]
  }

  selectedSearchOptionGroup.value = group.name
  selectedSearchOptionRoute.value = tab.route

  options[currentIndex].scrollIntoView({ block: 'nearest' })
}

const navigateKeyUp = () => {
  let options = document.querySelectorAll('.hover\\:bg-blue-600')

  if (options.length === 0) return

  let currentIndex = 0
  options.forEach((option, index) => {
    if (option.classList.contains('bg-blue-600')) {
      currentIndex = index
    }
    option.classList.remove('bg-blue-600', 'text-white')
    option.classList.add('text-gray-600')
  })

  options[currentIndex].classList.add('bg-blue-600', 'text-white')

  let childElement
  if (options[currentIndex].tagName === 'A') {
    childElement = options[currentIndex]
  } else {
    childElement = options[currentIndex].querySelector('a')
  }

  let indexGroup = Number(childElement?.getAttribute('group'))
  let indexTab = Number(childElement?.getAttribute('tab'))
  let indexItem = Number(childElement?.getAttribute('item'))

  let group = menuSearch.value[indexGroup]
  if (!group || !group.children) return

  let tab = group?.children[indexTab]

  if (tab.children) {
    tab = tab.children[indexItem]
  }

  selectedSearchOptionGroup.value = group.name
  selectedSearchOptionRoute.value = tab.route

  options[currentIndex].scrollIntoView({ block: 'nearest' })
}
</script>
<template>
  <div
    class="fixed z-50 inset-0 overflow-hidden bg-gray-500 bg-opacity-75 transition-opacity"
    v-show="modalActive">
    <div
      class="w-screen h-full flex pt-10 justify-start flex-col items-center relative align-bottom rounded-lg overflow-hidden shadow-2xl transform transition-all px-3">
      <div
        class="card overflow-hidden rounded-lg bg-white relative w-full md:w-2/3 2xl:w-1/3"
        id="modalSearch">
        <div
          class="content-modal flex flex-col items-center py-5 pr-3 pl-5 overflow-none"
          style="max-height: calc(100vh - 11rem)">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 ml-1 w-full"
            v-model="search"
            id="inputSearch"
            @keydown.up.prevent="navigateOptions('up')"
            @keydown.down.prevent="navigateOptions('down')"
            @keydown.tab.prevent="
              navigateOptions($event.shiftKey ? 'up' : 'down')
            "
            @keydown.enter.prevent="
              ;(activeMenu = selectedSearchOptionGroup),
                changeRoute(selectedSearchOptionRoute),
                closeModal()
            "
            @keyup="searchMenu" />

          <div class="absolute right-5 top-7 z-50 cursor-pointer">
            <XIcon
              class="text-gray-400 hover:text-gray-500"
              @click=";(search = ''), (clear = false), (menuSearch = '')"
              :class="{ hidden: !clear, block: clear }" />
          </div>
          <div
            v-if="menuSearch !== ''"
            class="overflow-y-auto scroll w-full mt-3 mr-1">
            <template v-if="menuSearch.length === 0">
              <div class="flex items-center justify-center gap-1 pt-3 pb-2">
                <XIcon class="w-10 h-10 text-red-800" />
                <p>
                  {{ t('components.sidebar.noData') }} <b>"{{ search }}"</b>
                </p>
              </div>
            </template>
            <template v-else v-for="(group, indexGroup) of menuSearch">
              <template
                v-if="
                  group.icon !== 'Home03Icon' && group.icon !== 'SearchLgIcon'
                ">
                <template v-for="(tab, indexTab) of group.children">
                  <div class="mb-2">
                    <p
                      v-if="tab.children"
                      class="text-blue-600 font-bold text-sm px-2 py-2.5">
                      {{ t(group.name) + ' > ' + t(tab.name) }}
                    </p>
                    <template v-else>
                      <p class="text-blue-600 font-bold text-sm px-2 py-2.5">
                        {{ t(group.name) }}
                      </p>
                      <a
                        :class="[
                          tab.children
                            ? ''
                            : 'hover:bg-blue-600 text-gray-600 hover:text-white rounded-md py-2 px-3 shadow-md border-t border-x border-gray-100 mb-1 relative cursor-pointer'
                        ]"
                        class="block"
                        :group="indexGroup"
                        :tab="indexTab"
                        :item="0"
                        @click="
                          ;(activeMenu = group.name),
                            changeRoute(tab.route),
                            closeModal()
                        ">
                        <span
                          class="absolute w-full top-0 left-0 text-blue-600 hover:text-white h-10 pt-2 pl-3">
                          »
                          <CornerDownLeftIcon
                            class="h-5 w-5 absolute top-0 right-0 mt-3 mr-2 text-white" />
                        </span>
                        <span class="px-4">{{ t(tab.name) }}</span>
                      </a>
                    </template>
                    <template v-for="(item, indexItem) of tab.children">
                      <div
                        class="hover:bg-blue-600 text-gray-600 hover:text-white rounded-md py-2 px-3 shadow-md border-t border-x border-gray-100 mb-1 cursor-pointer">
                        <a
                          class="block relative"
                          :group="indexGroup"
                          :tab="indexTab"
                          :item="indexItem"
                          @click="
                            ;(activeMenu = group.name),
                              changeRoute(item.route),
                              closeModal()
                          ">
                          <span
                            class="absolute text-blue-600 hover:text-white h-10 pt-2 pl-3"
                            style="width: 103%; top: -9px; left: -12px">
                            »
                            <CornerDownLeftIcon
                              class="h-5 w-5 absolute top-0 right-0 mt-3 mr-2 text-white" />
                          </span>
                          <span class="px-4">{{ t(item.name) }}</span>
                        </a>
                      </div>
                    </template>
                  </div>
                </template>
              </template>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    ref="sidebar"
    class="flex h-screen shadow-md shadow-gray-700 z-40"
    @focusin="sidebarFocused = true"
    @focusout=";(sidebarFocused = false), onCloseSidebar()"
    tabindex="0">
    <!-- NAV VISIBLE -->

    <div
      class="nav-main flex flex-col items-center h-screen w-16 sm:w-20 bg-blue-600 z-40">
      <div
        class="nav-main-logo mt-4 relative flex justify-center items-center p-4 select-none">
        <Popper
          arrow
          offsetDistance="18"
          :content="t('components.sidebar.home')"
          :hover="true"
          placement="left"
          class="tooltip">
          <img
            @click=";(activeMenu = ''), changeRoute('/home'), onCloseSidebar()"
            src="../../assets/icon-aps.svg"
            class="text-white w-9 cursor-pointer"
            :alt="t('components.sidebar.logo')" />
        </Popper>
      </div>
      <div
        class="nav-main-menu overflow-x-hidden overflow-y-auto h-full scroll-sidebar">
        <ul>
          <li
            v-for="(menuItem, i) of menu"
            :key="i"
            class="flex mt-2 flex-col justify-center w-12 h-12 items-center rounded-md cursor-pointer"
            :class="[
              menuItem.openSearch
                ? 'mb-6 text-blue-100'
                : activeMenu === menuItem.name
                ? 'bg-white text-blue-800'
                : 'text-white'
            ]"
            @click="openSidebar(menuItem.children, menuItem)">
            <Popper
              arrow
              offsetDistance="18"
              :content="t(menuItem.name)"
              :hover="true"
              placement="left"
              class="tooltip">
              <DIcon
                v-if="menuItem.icon"
                :name="menuItem.icon"
                class="h-6 w-6" />
            </Popper>
          </li>
        </ul>
      </div>
      <div
        class="nav-main-footer relative mt-auto flex flex-col items-center justify-around px-2 mb-3 select-none">
        <div>
          <div
            @click="emitSupport"
            class="flex flex-col justify-center items-center rounded-md hover:bg-blue-500 text-blue-300 hover:text-lime-50 w-12 h-12 cursor-pointer">
            <Popper
              offsetDistance="18"
              arrow
              :content="t('components.sidebar.support')"
              :hover="true"
              placement="right"
              class="tooltip">
              <LifeBuoy01Icon class="w-7 h-7" />
            </Popper>
          </div>
          <div
            @click="emitConfig"
            class="flex flex-col justify-center items-center mt-1 rounded-md hover:bg-blue-500 text-blue-300 hover:text-lime-50 w-12 h-12 cursor-pointer">
            <Popper
              offsetDistance="18"
              arrow
              :content="t('components.sidebar.updateInformation')"
              :hover="true"
              placement="right"
              class="tooltip">
              <Settings01Icon class="w-7 h-7" />
            </Popper>
          </div>
        </div>
        <hr class="border-blue-200 w-full my-5" />
        <div
          class="bg-white rounded-full select-none mb-4 mt-2 overflow-hidden cursor-pointer"
          @click="openSidebar([], '')">
          <User03Icon class="w-12 h-12 text-gray-300" />
        </div>
      </div>
    </div>
    <!-- NAV INVISIBLE -->
    <div
      class="nav-secondary h-screen flex flex-col w-56 sm:w-72 bg-white p-4 z-30"
      id="nav-secondary">
      <div class="nav-secondary-open relative flex justify-end">
        <span class="mt-4 mb-5">
          <Pin01Icon
            v-if="isPinUp"
            @click="pinUp"
            class="w-5 text-blue-700 cursor-pointer" />
          <Pin02Icon
            v-else
            @click="pinUp"
            class="w-5 text-gray-300 cursor-pointer" />
        </span>
      </div>
      <div
        class="nav-secondary-menu flex flex-col overflow-hidden overflow-y-auto scroll pr-2"
        style="margin-right: -8px">
        <template v-for="(menuItem, i) of menu" :key="i">
          <ul
            :style="
              activeMenu === menuItem.name ? 'display: block' : 'display: none'
            ">
            <li
              class="mt-1"
              v-for="(subMenu, index) of menuItem.children"
              :key="index">
              <div
                :data-id="`children${subMenu.idMenu}${index}`"
                @click="
                  subMenu.children
                    ? showChildrens(
                        `children${subMenu.idMenu}${index}`,
                        `arrow-menu${subMenu.idMenu}${index}`,
                        subMenuItems[index].children?.length
                      )
                    : changeRoute(subMenu.route)
                "
                class="rounded-md cursor-pointer"
                :class="[
                  subMenu.route === activeRoute ? 'bg-blue-600' : 'bg-gray-50',
                  subMenu.route === activeRoute ? 'text-white' : 'text-blue-700'
                ]">
                <div
                  class="flex h-11 justify-between items-center tracking-wider font-bold px-2 border border-transparent hover:border-blue-700 rounded-md">
                  <span class="select-none truncate">{{
                    t(subMenu.name)
                  }}</span>
                  <div class="flex">
                    <ChevronDownIcon
                      v-if="subMenu.children"
                      class="arrow-menu w-5"
                      :id="`arrow-menu${subMenu.idMenu}${index}`"
                      :data-id="`children${subMenu.idMenu}${index}`" />
                  </div>
                </div>
              </div>
              <!-- LISTA DE OPCIONES DE MENU -->
              <ul
                class="showItems"
                :id="`children${subMenu.idMenu}${index}`"
                style="height: 0px">
                <li
                  class="mt-1"
                  @click="changeRoute(thirdMenu.route)"
                  v-for="(thirdMenu, index) of subMenu?.children"
                  :key="index">
                  <div
                    class="h-11 flex items-center rounded-md border border-transparent hover:border-blue-600 font-medium cursor-pointer"
                    :title="t(thirdMenu.name)"
                    :class="[
                      thirdMenu.route === activeRoute
                        ? 'bg-blue-600'
                        : 'bg-gray-50',
                      thirdMenu.route === activeRoute
                        ? 'text-white'
                        : 'text-blue-700'
                    ]">
                    <p class="ml-8 mr-8 select-none truncate">
                      {{ t(thirdMenu.name) }}
                    </p>
                  </div>
                </li>
              </ul>
            </li>
          </ul>
        </template>
      </div>
      <div
        class="nav-secondary-footer relative mt-auto pt-5 pb-2 flex flex-row items-end justify-between">
        <div>
          <Popper
            arrow
            :content="t('components.sidebar.changeRole')"
            :hover="true"
            placement="right"
            class="tooltip">
            <p
              @click="emitRole"
              class="select-none font-medium text-base hover:underline hover:text-blue-700 cursor-pointer">
              {{ user?.role }}
            </p>
          </Popper>
          <p
            class="select-none text-blue-700 font-semibold text-ellipsis whitespace-nowrap overflow-hidden w-44 sm:w-52">
            {{ user?.name }}
          </p>
          <p
            class="select-none text-gray-500 text-ellipsis whitespace-nowrap overflow-hidden w-44 sm:w-52">
            {{ user?.email }}
          </p>
        </div>
        <Popper
          arrow
          :content="t('components.sidebar.signOff')"
          :hover="true"
          placement="right"
          class="tooltip">
          <LogOut03Icon
            class="w-6 mb-0.5 text-blue-600 self-end cursor-pointer"
            @click="emitLogout" />
        </Popper>
      </div>
    </div>
  </div>
</template>
<style scoped>
#nav-secondary {
  margin-left: -288px;
  -webkit-transition: margin 0.8s;
  transition: margin 0.8s;
}
@media (max-width: 639px) {
  #nav-secondary {
    margin-left: -224px;
  }
}
.showItems {
  overflow: hidden;
  cursor: pointer;
  transition: height 0.3s linear 0s;
}
.arrow-menu {
  display: block;
  transition: all 0.4s ease;
}
.arrow-menu.rotateArrow {
  transform: rotate(180deg);
}
</style>
